<template>
  <div class="flex w-full h-full overflow-hidden">
    <!-- IMAGES & REQUESTS -->
    <div class="flex flex-col w-2/3 h-full p-7 overflow-hidden">
      <!-- TOOLBAR (TABS) -->
      <!-- <div class="flex w-full h-8">
          <tx-tabs v-model="activeTab" class="grow" :tabs="Object.values(tabs)" />
          <settings-dropdown module="articleDetails" :disable-live-updates="!allowArticleCacheUpdatedEventStream" @change="onSettingsChanged" />
        </div> -->
      <div class="h-full">
        <div class="flex flex-col mt-2 h-full">
          <!-- HEADER -->
          <div class="flex flex-col w-full">
            <div class="w-full pb-2">
              <span class="inline-block w-5/6 text-2xl font-semibold select-all whitespace-nowrap text-ellipsis">{{ asset.document.Metadata?.ColorwayName || asset.document.FileName }}</span>
            </div>
          </div>

          <!-- Assets -->
          <div class="flex flex-1 w-full pt-1 overflow-hidden">
            <pan-zoom>
              <img :src="imgSrc" class="max-w-[600px]" alt="Asset Image">
            </pan-zoom>
          </div>
        </div>
      </div>
    </div>

    <!-- ARTICLE DETAILS AND FORMS -->
    <div class="w-1/3 h-full px-2 py-4 bg-default flex flex-col overflow-hidden">
      <tx-tabs v-model="activeTab" :tabs="tabs" class="mt-3 mb-2" />
      <!-- BODY -->
      <div v-if="activeTab === 'metadata'" class="w-full h-full pb-4 mb-2 mt-7 overflow-hidden">
        <div class="w-full h-full overflow-y-auto mt-4">
          <tx-input v-model="filter" type="text" placeholder="Filter attributes..." class="mb-4" />
          <!-- ATTRIBUTES -->
          <div v-for="att in filteredAttributes" :key="att.name" class="pb-6 w-full">
            <!-- LABEL -->
            <div class="text-md font-medium text-default pb-1.5" v-text="att.label" />
            <!-- ATTRIBUTE VALUE -->
            <div class="mr-2 text-base text-secondary select-all" v-text="!att.value || isEmpty(att.value) ? '-' : att.value" />
          </div>
        </div>
      </div>
      <div v-if="activeTab === 'aiVision'" class="w-full h-full pb-4 mb-2 mt-7">
        <div class="w-full h-full overflow-y-auto mt-4">
          <!-- ATTRIBUTES -->
          <div class="pb-6 w-full">
            <!-- LABEL -->
            <div class="text-md font-medium text-default pb-1.5" v-text="t('general.details')" />
            <!-- ATTRIBUTE VALUE -->
            <div class="mr-2 text-base text-secondary select-all whitespace-pre-line" v-text="asset.document.Vision" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { isEmpty } from 'lodash-es'
import { computed, ref } from 'vue'
import type { SearchAssetModel } from '@/api/dune/model/searchAssetModel'
import appConfig from '@/services/appConfig'
import TxInput from '@/shared/components/TxInput.vue'
import PanZoom from '@/shared/components/PanZoom.vue'
import { useUserStore } from '@/store/userData'
import TxTabs from '@/shared/components/TxTabs.vue'

interface IProps {
  asset: SearchAssetModel
  context: string
  contextKey: string
}

const props = defineProps<IProps>()
const { t } = useI18n()
const userStore = useUserStore()

const filter = ref('')
const activeTab = ref('metadata')
const tabs = [
  { key: 'metadata', label: 'assetsList.metadata', visible: true },
  { key: 'aiVision', label: 'assetsList.aiVision', visible: true },
]

const staticDefaultValues = [
  { name: 'FileName', label: t('assetsList.fileName'), value: props.asset.document.FileName },
  { name: 'Format', label: t('assetsList.format'), value: props.asset.document.Format },
  { name: 'Size', label: t('assetsList.fileSize'), value: props.asset.document.Size },
  { name: 'CreatedOn', label: t('assetsList.createdOn'), value: props.asset.document.CreatedOn },
  { name: 'UpdatedOn', label: t('assetsList.updatedOn'), value: props.asset.document.UpdatedOn },
  { name: 'Brand', label: 'Brand', value: props.asset.document.Metadata?.Brand },
  // Add the following metadata attributes if they exist: BrandedContent, ColorFamily, ColorwayCode, ColorwayName, Denim, Destruction, ExtendedSizes, FabricAfterWashWeight, FabricArchitecture, FabricMill, FabricStory, FabricStretchPerc, FinishType, GenericContent, HemTreatment, LookColorCode, LookColorName, MaterialCode, MaterialType, Merchant, Performance, PrimaryFabric, PrimaryFinish, ProductCode, ProductName, ProductPricePositioning, Season, Selvedge, StretchBucket, StretchType, SustainabilityType, TargetCOGS, TargetFOB, TargetGlobalMSRP, Waterless
  { name: 'BrandedContent', label: 'Branded Content', value: props.asset.document.Metadata?.BrandedContent },
  { name: 'ColorFamily', label: 'Color Family', value: props.asset.document.Metadata?.ColorFamily },
  { name: 'ColorwayCode', label: 'Colorway Code', value: props.asset.document.Metadata?.ColorwayCode },
  { name: 'ColorwayName', label: 'Colorway Name', value: props.asset.document.Metadata?.ColorwayName },
  { name: 'Denim', label: 'Denim', value: props.asset.document.Metadata?.Denim },
  { name: 'Destruction', label: 'Destruction', value: props.asset.document.Metadata?.Destruction },
  { name: 'ExtendedSizes', label: 'Extended Sizes', value: props.asset.document.Metadata?.ExtendedSizes },
  { name: 'FabricAfterWashWeight', label: 'Fabric After Wash Weight', value: props.asset.document.Metadata?.FabricAfterWashWeight },
  { name: 'FabricArchitecture', label: 'Fabric Architecture', value: props.asset.document.Metadata?.FabricArchitecture },
  { name: 'FabricMill', label: 'Fabric Mill', value: props.asset.document.Metadata?.FabricMill },
  { name: 'FabricStory', label: 'Fabric Story', value: props.asset.document.Metadata?.FabricStory },
  { name: 'FabricStretchPerc', label: 'Fabric Stretch Percentage', value: props.asset.document.Metadata?.FabricStretchPerc },
  { name: 'FinishType', label: 'Finish Type', value: props.asset.document.Metadata?.FinishType },
  { name: 'GenericContent', label: 'Generic Content', value: props.asset.document.Metadata?.GenericContent },
  { name: 'HemTreatment', label: 'Hem Treatment', value: props.asset.document.Metadata?.HemTreatment },
  { name: 'LookColorCode', label: 'Look Color Code', value: props.asset.document.Metadata?.LookColorCode },
  { name: 'LookColorName', label: 'Look Color Name', value: props.asset.document.Metadata?.LookColorName },
  { name: 'MaterialCode', label: 'Material Code', value: props.asset.document.Metadata?.MaterialCode },
  { name: 'MaterialType', label: 'Material Type', value: props.asset.document.Metadata?.MaterialType },
  { name: 'Merchant', label: 'Merchant', value: props.asset.document.Metadata?.Merchant },
  { name: 'Performance', label: 'Performance', value: props.asset.document.Metadata?.Performance },
  { name: 'PrimaryFabric', label: 'Primary Fabric', value: props.asset.document.Metadata?.PrimaryFabric },
  { name: 'PrimaryFinish', label: 'Primary Finish', value: props.asset.document.Metadata?.PrimaryFinish },
  { name: 'ProductCode', label: 'Product Code', value: props.asset.document.Metadata?.ProductCode },
  { name: 'ProductName', label: 'Product Name', value: props.asset.document.Metadata?.ProductName },
  { name: 'ProductPricePositioning', label: 'Product Price Positioning', value: props.asset.document.Metadata?.ProductPricePositioning },
  { name: 'Season', label: 'Season', value: props.asset.document.Metadata?.Season },
  { name: 'Selvedge', label: 'Selvedge', value: props.asset.document.Metadata?.Selvedge },
  { name: 'StretchBucket', label: 'Stretch Bucket', value: props.asset.document.Metadata?.StretchBucket },
  { name: 'StretchType', label: 'Stretch Type', value: props.asset.document.Metadata?.StretchType },
  { name: 'SustainabilityType', label: 'Sustainability Type', value: props.asset.document.Metadata?.SustainabilityType },
  { name: 'TargetCOGS', label: 'Target COGS', value: props.asset.document.Metadata?.TargetCOGS },
  { name: 'TargetFOB', label: 'Target FOB', value: props.asset.document.Metadata?.TargetFOB },
  { name: 'TargetGlobalMSRP', label: 'Target Global MSRP', value: props.asset.document.Metadata?.TargetGlobalMSRP },
  { name: 'Waterless', label: 'Waterless Process Used?', value: props.asset.document.Metadata?.Waterless },
]

const imgSrc = computed(() => {
  const params = new URLSearchParams()
  params.set('Context', props.context)
  params.set('ContextKey', props.contextKey)
  params.set('w', '600')
  params.set('trim', 'false')
  params.set('f', 'webp')

  return `${appConfig.AssetsUrl}/assets/content/${props.asset.document.StorageFile.toUpperCase()}?${params.toString()}`
})

const attributes = computed(() => {
  let assetDetailsConfig: any = staticDefaultValues

  // Check for catalog or seller configurations
  if (userStore.activeCatalog?.Config?.DigitalAssetsDetailsTemplate?.length) {
    assetDetailsConfig = userStore.activeCatalog.Config.DigitalAssetsDetailsTemplate.map((att) => {
      return {
        name: att.name,
        label: att.label,
        value: resolveAttributeValuePath(att.value),
      }
    })
  }
  else if (userStore.sellerConfig?.DigitalAssetsDetailsTemplate?.length) {
    assetDetailsConfig = userStore.sellerConfig.DigitalAssetsDetailsTemplate.map((att) => {
      return {
        name: att.name,
        label: att.label,
        value: resolveAttributeValuePath(att.value),
      }
    })
  }

  return assetDetailsConfig
})

function resolveAttributeValuePath(path: string): any {
  if (!path) { return undefined }

  const endPoint = path.startsWith('document.')
    ? path.substring('document.'.length)
    : path
  const pathParts = endPoint.split('.')
  let current: any = props.asset.document
  for (const part of pathParts) {
    if (current && Object.prototype.hasOwnProperty.call(current, part)) {
      current = current[part]
    }
    else {
      return '-'
    }
  }

  return current
}

const filteredAttributes = computed(() => {
  return attributes.value.filter(att => (att.label && att.label.toString().toLowerCase().includes(filter.value.toLowerCase())) || (att.value && att.value.toString().toLowerCase().includes(filter.value.toLowerCase())))
})
</script>
